import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  Badge,
  alpha,
  Tooltip
} from '@mui/material';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Login as LoginIcon,
  Create as CreateIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';

const NavBar = () => {
  const [user] = useAuthState(auth);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const open = Boolean(anchorEl);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.signOut();
    handleClose();
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: '/', label: 'Home', icon: <HomeIcon /> },
    ...(user ? [
      { path: '/admin', label: 'Dashboard', icon: <DashboardIcon /> },
      { path: '/upload-form', label: 'Create', icon: <CreateIcon /> },
      { path: '/my-submissions', label: 'Submissions', icon: <AssignmentIcon /> }
    ] : [])
  ];

  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ py: 1 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/"
              sx={{ 
                fontSize: { xs: '1.1rem', md: '1.4rem' }, 
                fontWeight: 'bold',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
                px: 2
              }}
            >
              FormPulse
            </Button>
            {user && (
              <Chip 
                label="Beta" 
                size="small" 
                sx={{ 
                  ml: 1, 
                  backgroundColor: alpha(theme.palette.common.white, 0.2),
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
            )}
          </Box>
          
          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 2 }}>
              {navItems.map((item) => (
                <Button 
                  key={item.path}
                  color="inherit" 
                  component={RouterLink} 
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    textTransform: 'none',
                    fontWeight: isActiveRoute(item.path) ? 'bold' : 'normal',
                    backgroundColor: isActiveRoute(item.path) 
                      ? alpha(theme.palette.common.white, 0.2) 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.1),
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    borderRadius: 2,
                    px: 2,
                    py: 1
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* User Menu */}
          {user ? (
            <>
              <Tooltip title="Account menu">
                <IconButton
                  onClick={handleMenu}
                  size="small"
                  sx={{ 
                    ml: 1,
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.1),
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isMobile ? (
                    <MenuIcon sx={{ color: 'white' }} />
                  ) : (
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: '#4caf50',
                            border: '2px solid white'
                          }}
                        />
                      }
                    >
                      <Avatar
                        src={user.photoURL}
                        alt={user.displayName}
                        sx={{ 
                          width: 36, 
                          height: 36,
                          border: '2px solid rgba(255, 255, 255, 0.3)'
                        }}
                        imgProps={{
                          crossOrigin: 'anonymous',
                          referrerPolicy: 'no-referrer'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                    </Badge>
                  )}
                </IconButton>
              </Tooltip>
              
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  elevation: 8,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    borderRadius: 2,
                    minWidth: 200,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {/* User Info */}
                <MenuItem sx={{ pointerEvents: 'none', py: 2 }}>
                  <Avatar 
                    src={user.photoURL}
                    imgProps={{
                      crossOrigin: 'anonymous',
                      referrerPolicy: 'no-referrer'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    sx={{ border: '2px solid', borderColor: 'primary.main' }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {user.displayName || 'User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                
                {/* Mobile Navigation Items */}
                {isMobile && (
                  <>
                    {navItems.map((item) => (
                      <MenuItem 
                        key={item.path}
                        component={RouterLink} 
                        to={item.path} 
                        onClick={handleClose}
                        sx={{
                          backgroundColor: isActiveRoute(item.path) 
                            ? alpha(theme.palette.primary.main, 0.1) 
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                      >
                        <Box sx={{ mr: 2, color: 'primary.main' }}>
                          {item.icon}
                        </Box>
                        {item.label}
                      </MenuItem>
                    ))}
                    <Divider />
                  </>
                )}
                
                {/* Profile & Admin */}
                <MenuItem 
                  component={RouterLink} 
                  to="/profile" 
                  onClick={handleClose}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                  Profile
                </MenuItem>
                
                {!isMobile && (
                  <MenuItem 
                    component={RouterLink} 
                    to="/admin" 
                    onClick={handleClose}
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    <AdminIcon sx={{ mr: 2, color: 'primary.main' }} />
                    Admin
                  </MenuItem>
                )}
                
                <Divider />
                <MenuItem 
                  onClick={handleLogout}
                  sx={{
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.05)
                    }
                  }}
                >
                  <LogoutIcon sx={{ mr: 2 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/login"
              startIcon={<LoginIcon />}
              variant="outlined"
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
                px: 3
              }}
            >
              Sign In
            </Button>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default NavBar;